import { observable, toJS, computed, autorun } from 'mobx';
import { DndComponentClass, ConnectDragSource } from 'react-dnd';
import { ComponentClass, Component, createElement } from 'react';
import { ReactComponentLike } from 'prop-types';
import { WithStyles } from 'react-jss';
import { Style } from 'jss';
import { CSSProperties } from 'jss/css';
import ReactDOMServer from 'react-dom/server';
import { deepObserve } from 'mobx-utils';

export interface ComponentToken {
    name: string;
    type: 'component' | 'column' | 'page_settings';
    shelfComponent: DndComponentClass<any>;
    screenComponent: ReactComponentLike;
    settingsComponent: ComponentClass<{ descriptor: any }>;
    emailComponent?: ReactComponentLike;
    defaultTemplate: any;
    svg: string;
}

export interface SettingToken {
    name: string;
    type: 'settings';
}

export interface MarginSettings {
    margin: number;
    marginLeft: number;
    marginRight: number;
    marginTop: number;
    marginBottom: number;
}

export interface ColorSettings {
    backgroundColor: number;
    color: number;
}

export interface PaddingSettings {
    padding: number;
    paddingLeft: number;
    paddingRight: number;
    paddingTop: number;
    paddingBottom: number;
}

export interface HeightWidthSettings {
    width: number;
    height: number;
}

export interface BorderSettings {
    border: number;
    borderRadius: number;
    borderTopLeftRadius: number;
    borderTopRightRadius: number;
    borderBottomRightRadius: number;
    borderBottomLeftRadius: number;
}

export interface FontSettings {
    fontSize: number;
    fontStyle: string;
    fontFamily: string;
    textAlign: string;
}

export function buttonDefaultSettings() {
    return {
        ...getDefaultSettings(),
        width: 100,
        height: 33,
        borderRadius: 2,
        fontWeight: 400,
        boxShadow: 'none',
        border: 0,
        textAlign: 'center',
        backgroundColor: '#20d29f',
        color: '#FFFFFF',
        borderStyle: 'hidden',
        fontSize: 12,
        padding: 6,
        margin: 4,
    };
}

export function socialDefaultSettings() {
    return {
        ...getDefaultSettings(),
        backgroundColor: '#bab7c7',
    };
}

export function imageDefaultSettings() {
    return {
        ...getDefaultSettings(),
        margin: 4,
        backgroundColor: '#D5EFF6',
    };
}

export function getDefaultSettings() {
    return {
        margin: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        padding: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5,
        borderBottomLeftRadius: 5,
        borderBottomRightRadius: 5,
    };
}

export type ComponentStyle = CSSProperties;

/**
 * MarginSettings &
 PaddingSettings &
 HeightWidthSettings &
 BorderSettings &
 ColorSettings &
 FontSettings
 */

export interface ComponentDesc<T> {
    columnSettings?: any;
    id?: number;
    type: string;
    style: CSSProperties;
    settings: T;
    name?: string;
    isStatic?: boolean;
}

export interface ColumnsProps {
    backgroundColor: string;
    contentBackgroundColor: string;
    padding: number;
    color: string;
    columns: ComponentDesc<any>[][][][];
    widths: number[];
}

export interface ShelfComponentProps<T extends Record<string, Style>> extends WithStyles<T> {
    connectDragSource: ConnectDragSource;
    isDragging: boolean;
}

export interface ScreenComponentProps<T> {
    descriptor: ComponentDesc<T>;
    connectDragSource?: ConnectDragSource;
    isDragging?: boolean;
}

export interface SettingsComponentProps<T, S extends Record<string, Style> = {}> extends WithStyles<S> {
    descriptor: ComponentDesc<T>;
}

export interface UIStore {
    prev_view: any;
    left_panel_view: string;
    isTestMailDialogOpen: boolean;
    isMailConfirmationDialogOpen: boolean;
    isTestSaveAsOpen: boolean;
    isPreviewOpen: boolean;
    isSettingsOpen: boolean;
    isHelpOpen: boolean;
    isConfirmationBoxOpen: boolean;
    dialogTitle: string;
    dialogContent: string;
    onDialogResponded: Function;
    onHover: boolean;
}

export interface PageSettings {
    backgroundColor?: string;
    color?: string;
    padding?: number;
    paddingTop?: number;
    paddingBottom?: number;
    paddingLeft?: number;
    paddingRight?: number;
    width?: number;
}

export interface ColumnSettings {
    backgroundColor?: string;
    color?: string;
    width?: number;
    padding?: number;
}

class AppStore {
    @observable
    components: ComponentToken[] = [];
    @observable
    settings: SettingToken[] = [];

    tempCard = null;

    @observable
    ui: UIStore = {
        prev_view: 'component',
        left_panel_view: 'component',
        isTestMailDialogOpen: false,
        isMailConfirmationDialogOpen: false,
        isTestSaveAsOpen: false,
        isHelpOpen: false,
        isConfirmationBoxOpen: false,
        dialogTitle: '',
        dialogContent: '',
        onDialogResponded: false,
        onHover: true,
    };

    @computed
    get isSettingsOpen() {
        return store.ui.left_panel_view === 'settings' || store.ui.left_panel_view === 'page_settings' || store.ui.left_panel_view === 'column_settings';
    }

    @observable
    currentTemplate: Template = this.createDefaultTemplate();

    // @observable
    // page: ComponentDesc<any>[] = [];
    @observable
    pageSettings: Partial<PageSettings> = {
        width: 600,
        padding: 0,
    };
    @observable
    columnSettings: Partial<ColumnSettings> = {
        width: 600,
        backgroundColor: 'none',
    };

    @observable
    rightPanelComponent: any;
    @observable
    rightPanelComponentDescriptor: any;
    @observable
    leftPanelComponent: any;
    @observable
    leftPanelComponentDescriptor: any;
    callbacks: StoreCallbacks = {};
    @observable
    templates: Template[] = [];
    currentColumn: any;

    shortcode(shortcode: any) {
        throw new Error('Method not implemented.');
    }

    createBlankTemplate() {
        return {
            id: +new Date(),
            name: 'New Template',
            rows: [],
            pageSettings: {
                width: 600,
                backgroundColor: '#fff',
                padding: 0,
                color: '#fff',
            },
        };
    }

    createDefaultTemplate() {
        return {
            id: 1561439157901,
            name: 'Default Template',
            rows: [
                {
                    id: 1561439159300,
                    type: 'SingleColumn',
                    style: {},
                    settings: {
                        backgroundColor: '#fff',
                        contentBackgroundColor: '#fff',
                        padding: 0,
                        color: '#000',
                        columns: [[]],
                        widths: [100],
                    },
                },
            ],
            pageSettings: {
                width: 600,
                backgroundColor: '#fff',
                padding: 0,
                color: '#fff',
            },
        };
    }

    registerSettings(s: SettingToken) {
        this.settings.push(s);
    }

    registerComponent(t: ComponentToken) {
        this.components.push(t);
    }

    getComponentTokenByName(type: string): ComponentToken {
        return this.components.filter((c) => c.name == type)[0];
    }

    /* selectComponent(descriptor: any) {
        if (descriptor) {
            this.leftPanelComponent = this.getSettingsComponentByType(
                descriptor.type
            );
            this.rightPanelComponentDescriptor = descriptor;
        } else {
            this.leftPanelComponent = null;
            this.rightPanelComponentDescriptor = null;
        }
    }*/

    getScreenComponentByType(type: string): ReactComponentLike {
        return this.components.filter((c) => c.name == type)[0].screenComponent;
    }

    getSettingsComponentByType(type: string): ComponentClass<{ descriptor: any }> {
        return this.components.filter((c) => c.name == type)[0].settingsComponent;
    }

    selectComponent(descriptor: any) {
        if (descriptor) {
            this.leftPanelComponent = this.getSettingsComponentByType(descriptor.type);
            this.leftPanelComponentDescriptor = descriptor;
            store.ui.left_panel_view = 'settings';
            store.getSelectedRow(descriptor.id);
        } else {
            this.leftPanelComponent = null;
            this.leftPanelComponentDescriptor = null;
            store.ui.left_panel_view = store.ui.prev_view;
        }
        if (store.ui.onHover === false) {
            store.ui.onHover = true;
        }
    }

    removeComponentFromPage(id: number) {
        let matched = this.currentTemplate.rows.filter((c) => c.id === id);
        if (matched && matched.length > 0) {
            return this.currentTemplate.rows.splice(this.currentTemplate.rows.indexOf(matched[0]), 1);
        }
        this.currentTemplate.rows
            .filter((c) => c.settings.columns)
            .forEach((c) => {
                let columns: ComponentDesc<any>[][] = c.settings.columns;
                columns.forEach((col) => {
                    let matched = col.filter((c) => c.id === id);
                    if (matched && matched.length > 0) {
                        return col.splice(col.indexOf(matched[0]), 1);
                    }
                    return null;
                });
            });
        return null;
    }

    renderTemplate() {
        return ReactDOMServer.renderToStaticMarkup(createElement(StaticPage, {}));
    }

    undo() {
        let last = history.pop();
        console.log('popped history item', last);
        if (last) {
            future.push(last);
            console.log('pushed future item', last);
            store.currentTemplate = last;
        }
    }

    redo() {
        let next = future.pop();
        console.log('popped future item', next);
        if (next) {
            store.currentTemplate = next;
            history.push(next);
            console.log('pushed history item', next);
        }
    }

    getHistory() {
        return history;
    }

    @observable
    selectedRow: ComponentDesc<any>;

    @observable
    selectedColumn: ComponentDesc<any>;

    getSelectedRow(elementId?: number, rowId?: number) {
        let selectedElementId: number = elementId ? elementId : this.leftPanelComponentDescriptor ? this.leftPanelComponentDescriptor.id : 0;
        for (let rowIdx in this.currentTemplate.rows) {
            let row = this.currentTemplate.rows[rowIdx];
            if (rowId && row.id == rowId) {
                this.selectedRow = row;
                return row;
            }
            for (let colIdx in row.settings.columns) {
                let column = row.settings.columns[colIdx];
                for (let elemIdx in column) {
                    let element = column[elemIdx];
                    if (element.id === selectedElementId) {
                        this.selectedRow = row;
                        return row;
                    }
                }
            }
        }
        return null;
    }

    //returns the index of selected row
    getRowIndex(rowId?: number) {
        for (let rowIdx in this.currentTemplate.rows) {
            let index = store.currentTemplate.rows.filter((c) => c.id === rowId);
            if (index && index.length > 0) {
                return this.currentTemplate.rows.indexOf(index[0]);
            }
        }
        return null;
    }
}

export class StaticPage extends Component {
    render() {
        return (
            <div
                style={{
                    minWidth: store.currentTemplate.pageSettings.width,
                    backgroundColor: store.currentTemplate.rows.backgroundColor,
                }}>
                {store.currentTemplate.rows.map((row) => {
                    let token = store.getComponentTokenByName(row.type);
                    let newRow = JSON.parse(JSON.stringify({ ...row, isStatic: true }));
                    newRow.settings.columns.forEach((c) => {
                        c.forEach((k) => (k.isStatic = true));
                    });
                    return createElement(token.emailComponent ? token.emailComponent : token.screenComponent, {
                        descriptor: newRow,
                        key: row.id,
                    });
                })}
            </div>
        );
    }
}

class StaticColumn extends Component {
    render() {
        return store.currentColumn.rows.map((row) => {
            let token = store.getComponentTokenByName(row.type);
            let newRow = { ...row, isStatic: true };
            return createElement(token.emailComponent ? token.emailComponent : token.screenComponent, {
                descriptor: newRow,
                key: row.id,
            });
        });
    }
}

export interface Template {
    id: number;
    name: string;
    rows: ComponentDesc<any>[];
    pageSettings: PageSettings;
    columnSettings: ColumnSettings;
    // TODO: add preview mechanisms
}

interface StoreCallbacks {
    saveCb?: (template: Template) => void;
    testMailCb?: (to: string, subject: string, body: string) => void;
    fetchOrdercb?: (order_id: number) => Promise<number>;
    doShortCode?: (shortcode: string) => Promise<string>;
}

export const store = new AppStore();

let history: any[] = [];
let future: any[] = [];
let currentState: any = null;

deepObserve(store.currentTemplate, (change) => {
    // console.log('change intercepted', change);
    let copy = { ...toJS(store.currentTemplate) };
    if (currentState) {
        history.push(copy);
    }
    currentState = copy;
    return change;
});

autorun(() => {
    if (store.currentTemplate.rows.length === 0) {
        store.currentTemplate = store.createDefaultTemplate();
    }
});
